/**
 * VendorIcon3D — Reusable 3D icon wrapper for Vendor Portal
 * Matches Patient Portal's PatientIcon3D visual language but tuned for ops density.
 */
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type VendorIconVariant = 'arrival' | 'departure' | 'rental' | 'tour' | 'stay' | 'neutral' | 'warning' | 'success';
export type VendorIconSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT_STYLES: Record<VendorIconVariant, string> = {
  arrival:
    'from-[hsl(215,95%,55%)] to-[hsl(225,90%,65%)] shadow-[0_4px_14px_-3px_hsl(220,90%,50%,0.40)]',
  departure:
    'from-[hsl(185,80%,42%)] to-[hsl(195,75%,55%)] shadow-[0_4px_14px_-3px_hsl(190,75%,40%,0.40)]',
  rental:
    'from-[hsl(155,70%,40%)] to-[hsl(165,65%,52%)] shadow-[0_4px_14px_-3px_hsl(160,65%,36%,0.40)]',
  tour:
    'from-[hsl(28,92%,56%)] to-[hsl(38,90%,62%)] shadow-[0_4px_14px_-3px_hsl(30,90%,48%,0.40)]',
  stay:
    'from-[hsl(270,70%,58%)] to-[hsl(290,65%,65%)] shadow-[0_4px_14px_-3px_hsl(275,65%,50%,0.40)]',
  neutral:
    'from-[hsl(220,15%,50%)] to-[hsl(220,12%,63%)] shadow-[0_4px_14px_-3px_hsl(220,12%,42%,0.30)]',
  warning:
    'from-[hsl(38,95%,50%)] to-[hsl(28,90%,58%)] shadow-[0_4px_14px_-3px_hsl(35,90%,45%,0.40)]',
  success:
    'from-[hsl(150,70%,40%)] to-[hsl(160,65%,52%)] shadow-[0_4px_14px_-3px_hsl(155,65%,36%,0.40)]',
};

/** Tint for inactive nav / summary labels */
export const VENDOR_VARIANT_TINT: Record<VendorIconVariant, string> = {
  arrival: 'bg-[hsl(215,90%,95%)] text-[hsl(215,95%,50%)]',
  departure: 'bg-[hsl(185,75%,94%)] text-[hsl(185,80%,38%)]',
  rental: 'bg-[hsl(155,65%,94%)] text-[hsl(155,70%,36%)]',
  tour: 'bg-[hsl(30,90%,94%)] text-[hsl(28,92%,50%)]',
  stay: 'bg-[hsl(270,65%,95%)] text-[hsl(270,70%,52%)]',
  neutral: 'bg-[hsl(220,12%,94%)] text-[hsl(220,15%,45%)]',
  warning: 'bg-[hsl(38,90%,94%)] text-[hsl(38,95%,42%)]',
  success: 'bg-[hsl(150,65%,93%)] text-[hsl(150,70%,36%)]',
};

const SIZE_STYLES: Record<VendorIconSize, { container: string; icon: string }> = {
  xs: { container: 'h-7 w-7 rounded-lg', icon: 'h-3.5 w-3.5' },
  sm: { container: 'h-9 w-9 rounded-xl', icon: 'h-4 w-4' },
  md: { container: 'h-11 w-11 rounded-xl', icon: 'h-5 w-5' },
  lg: { container: 'h-14 w-14 rounded-2xl', icon: 'h-7 w-7' },
};

interface VendorIcon3DProps {
  icon: LucideIcon;
  variant?: VendorIconVariant;
  size?: VendorIconSize;
  className?: string;
}

export default function VendorIcon3D({
  icon: Icon,
  variant = 'neutral',
  size = 'sm',
  className,
}: VendorIcon3DProps) {
  const sizeStyle = SIZE_STYLES[size];

  return (
    <div
      className={cn(
        'relative flex items-center justify-center bg-gradient-to-br shrink-0',
        sizeStyle.container,
        VARIANT_STYLES[variant],
        className,
      )}
    >
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-[40%] rounded-[inherit] bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
      <Icon className={cn(sizeStyle.icon, 'text-white relative z-10 drop-shadow-sm')} strokeWidth={2.2} />
    </div>
  );
}
